import { Dispatch, SetStateAction } from 'react';
import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { FlexContainer } from 'components/common';
import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalPortal
} from 'components/common/modal';
import {
  CalendarEventParticipantAddSelf,
  CalendarEventParticipantAddSelfUnauthenticated,
  CalendarEventParticipantRemoveSelf,
  CalendarEventQuickNavigation,
  CalendarEventRenderInfo
} from '.';

import type { CalendarApi } from '@fullcalendar/core';
import type { CalendarEventExtended } from 'shared-types';
import type { GetCalendarEventAdditionalRenderingInfoRes } from 'shared-types/responses';

interface Props {
  calendarApi?: CalendarApi;
  event?: CalendarEventExtended;
  setEvent: Dispatch<SetStateAction<CalendarEventExtended>>;
}

const CalendarEventModal = ({ calendarApi, event, setEvent }: Props) => {
  const { messages, user } = useReduxState();

  const userIsOwnerOrSuperadmin =
    event?.userIsOwner || user?.roles.includes('superadmin');

  const hasPrivileges =
    event?.visibility === 'public' ||
    userIsOwnerOrSuperadmin ||
    event?.userStatus === 'added';

  const { data, loading } =
    useApiData<GetCalendarEventAdditionalRenderingInfoRes>({
      allowUnauthenticatedUser: true,
      onlyIf: Boolean(event?.id),
      url: `/admin/calendar-events/${event?.id}/additional-info`
    });

  const handleHide = () => setEvent(null);

  return !event ? null : (
    <ModalPortal>
      <Modal
        closeButton={true}
        onHide={handleHide}
        title={messages.eventCalendarEventDetails}
        visible={Boolean(event)}
        width="wide"
      >
        <ModalContent style={{ padding: 0 }}>
          <CalendarEventRenderInfo
            event={event}
            eventAchievements={data?.achievements || []}
            eventCompetencies={data?.competencies || []}
            eventAttachments={data?.attachments || []}
            hasPrivileges={hasPrivileges}
            infoLoading={loading}
            setEvent={setEvent}
          />
        </ModalContent>
        <ModalFooter>
          <FlexContainer style={{ gap: 10, justifyContent: 'flex-end' }}>
            {event.userStatus && !event.userIsOwner ? (
              <CalendarEventParticipantRemoveSelf
                calendarApi={calendarApi}
                event={event}
                setEvent={setEvent}
              />
            ) : null}

            {!event.userStatus && !event.userIsOwner ? (
              user ? (
                <CalendarEventParticipantAddSelf
                  calendarApi={calendarApi}
                  event={event}
                  setEvent={setEvent}
                />
              ) : (
                <CalendarEventParticipantAddSelfUnauthenticated
                  calendarApi={calendarApi}
                  event={event}
                  setEvent={setEvent}
                />
              )
            ) : null}

            {userIsOwnerOrSuperadmin ||
            (event.userStatus && event.userStatus !== 'queued') ? (
              <CalendarEventQuickNavigation event={event} />
            ) : null}
          </FlexContainer>
        </ModalFooter>
      </Modal>
    </ModalPortal>
  );
};

export default CalendarEventModal;
